import { __awaiter } from '../_virtual/_tslib.js';
import { waitForInputFocusZoomReset, syncViewportHeightNow } from './viewportSheet.js';
export { getVisualViewportHeight, isPageInputFocusZoomActive, syncViewportHeightNow, syncVisualViewportHeightVar, waitForInputFocusZoomReset } from './viewportSheet.js';
import { isMobileViewport } from './browser.js';

const scrollToElementById = (elementId, { behavior = "smooth", block = "start", focus = true } = {}) => {
    const element = document.getElementById(elementId);
    if (!element) {
        return false;
    }
    element.scrollIntoView({ behavior, block });
    if (focus) {
        element.focus({ preventScroll: true });
    }
    return true;
};
const getRouteHash = (asPath) => asPath.split("#")[1];
/**
 * Zoom is pinned so focusing a field cannot scale the page under a sheet.
 *
 * `interactive-widget` must stay `resizes-content`: `overlays-content` leaves both the layout
 * and visual viewport at full height when the keyboard opens, which pins a sheet's sticky
 * footer under the keyboard and zeroes out every keyboard scroll measurement.
 */
const VIEWPORT_ZOOM_DISABLED_CONTENT = "width=device-width,minimum-scale=1,maximum-scale=1,initial-scale=1,user-scalable=no,interactive-widget=resizes-content";
const getScrollbarWidth = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
    }
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
};
/** Blur the focused control so iOS Safari can exit input-focus zoom before a sheet opens. */
const blurActiveFormControl = () => {
    if (typeof document === "undefined") {
        return;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && active !== document.documentElement) {
        active.blur();
    }
};
/** Blur + wait for iOS input-focus zoom reset before mounting a mobile full-viewport sheet. */
const prepareMobileSheetOpen = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!isMobileViewport()) {
        return;
    }
    blurActiveFormControl();
    yield waitForInputFocusZoomReset();
});
const lockBodyScroll = (options) => {
    var _a;
    const { style: bodyStyle } = document.body;
    const { style: htmlStyle } = document.documentElement;
    const scrollY = window.scrollY;
    const scrollbarWidth = getScrollbarWidth();
    const previous = {
        bodyOverflow: bodyStyle.overflow,
        bodyPaddingRight: bodyStyle.paddingRight,
        bodyPosition: bodyStyle.position,
        bodyTop: bodyStyle.top,
        bodyLeft: bodyStyle.left,
        bodyWidth: bodyStyle.width,
        htmlOverflow: htmlStyle.overflow,
        scrollY
    };
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = (options === null || options === void 0 ? void 0 : options.disableZoom) ? (_a = viewportMeta === null || viewportMeta === void 0 ? void 0 : viewportMeta.getAttribute("content")) !== null && _a !== void 0 ? _a : null : null;
    if ((options === null || options === void 0 ? void 0 : options.disableZoom) && viewportMeta) {
        blurActiveFormControl();
        viewportMeta.setAttribute("content", VIEWPORT_ZOOM_DISABLED_CONTENT);
        const syncAfterMetaSwap = () => syncViewportHeightNow();
        requestAnimationFrame(syncAfterMetaSwap);
        [50, 150, 300, 500].forEach((delayMs) => {
            window.setTimeout(syncAfterMetaSwap, delayMs);
        });
    }
    if (options === null || options === void 0 ? void 0 : options.freezeOnMobile) {
        bodyStyle.position = "fixed";
        bodyStyle.top = `-${scrollY}px`;
        bodyStyle.left = "0";
        bodyStyle.width = "100%";
        bodyStyle.overflow = "hidden";
        htmlStyle.overflow = "hidden";
    }
    else {
        bodyStyle.overflow = "hidden";
        htmlStyle.overflow = "hidden";
        if (scrollbarWidth > 0) {
            bodyStyle.paddingRight = `${scrollbarWidth}px`;
        }
    }
    const restoreScrollPosition = () => {
        window.scrollTo(0, previous.scrollY);
        requestAnimationFrame(() => {
            window.scrollTo(0, previous.scrollY);
        });
    };
    return () => {
        bodyStyle.overflow = previous.bodyOverflow;
        bodyStyle.paddingRight = previous.bodyPaddingRight;
        bodyStyle.position = previous.bodyPosition;
        bodyStyle.top = previous.bodyTop;
        bodyStyle.left = previous.bodyLeft;
        bodyStyle.width = previous.bodyWidth;
        htmlStyle.overflow = previous.htmlOverflow;
        if (options === null || options === void 0 ? void 0 : options.freezeOnMobile) {
            restoreScrollPosition();
        }
        if ((options === null || options === void 0 ? void 0 : options.disableZoom) && viewportMeta && previousViewportContent) {
            viewportMeta.setAttribute("content", previousViewportContent);
        }
        if (options === null || options === void 0 ? void 0 : options.freezeOnMobile) {
            requestAnimationFrame(restoreScrollPosition);
        }
    };
};
const scrollToElementByIdFromHash = (asPath, elementId) => {
    if (getRouteHash(asPath) !== elementId) {
        return;
    }
    requestAnimationFrame(() => scrollToElementById(elementId));
};

export { blurActiveFormControl, getRouteHash, getScrollbarWidth, lockBodyScroll, prepareMobileSheetOpen, scrollToElementById, scrollToElementByIdFromHash };
//# sourceMappingURL=dom.js.map
